import { httpClient } from './http-client';

export interface PushSubscriptionDto {
  endpoint: string;
  p256dhKey: string;
  authKey: string;
  userAgent?: string;
}

export interface NotificationItem {
  id: string;
  userId: string;
  channel: 'IN_APP' | 'EMAIL' | 'SMS' | 'PUSH' | 'WHATSAPP';
  title: string;
  message: string;
  data?: Record<string, any> | null;
  status: 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
  sentAt?: string | null;
  deliveredAt?: string | null;
  readAt?: string | null;
  createdAt: string;
}

export interface PaginatedNotifications {
  items: NotificationItem[];
  total: number;
}

export interface NotificationPreference {
  id: string;
  userId: string;
  emailEnabled: boolean;
  smsEnabled: boolean;
  pushEnabled: boolean;
  whatsappEnabled: boolean;
  inAppEnabled: boolean;
  appointmentReminders: boolean;
  marketingEnabled: boolean;
}

export const notificationsApi = {
  getNotifications: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    channel?: string;
  }): Promise<PaginatedNotifications> => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', String(params.page));
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.status) query.append('status', params.status);
    if (params?.channel) query.append('channel', params.channel);

    const queryString = query.toString();
    return httpClient.get<PaginatedNotifications>(
      `/notifications${queryString ? `?${queryString}` : ''}`
    );
  },

  markAsRead: async (id: string): Promise<NotificationItem> => {
    return httpClient.patch<NotificationItem>(`/notifications/${id}/read`, {});
  },

  markAllAsRead: async (): Promise<{ count: number }> => {
    return httpClient.post<{ count: number }>('/notifications/read-all', {});
  },

  deleteNotification: async (id: string): Promise<void> => {
    return httpClient.delete<void>(`/notifications/${id}`);
  },

  getPreferences: async (): Promise<NotificationPreference | null> => {
    return httpClient.get<NotificationPreference | null>('/notifications/preferences');
  },

  updatePreferences: async (
    data: Partial<NotificationPreference>
  ): Promise<NotificationPreference> => {
    return httpClient.put<NotificationPreference>('/notifications/preferences', data);
  },

  getVapidPublicKey: async (): Promise<{ publicKey: string }> => {
    return httpClient.get<{ publicKey: string }>('/notifications/push-subscription/public-key', {
      requiresAuth: false,
    });
  },

  registerPushSubscription: async (data: PushSubscriptionDto): Promise<{ success: boolean }> => {
    return httpClient.post<{ success: boolean }>('/notifications/push-subscription', data);
  },

  unregisterPushSubscription: async (endpoint: string): Promise<{ success: boolean }> => {
    return httpClient.delete<{ success: boolean }>('/notifications/push-subscription', {
      data: { endpoint },
    });
  },
};
